/**
 * The rule that checks whether a chart draws what it claims.
 *
 * A chart is the one thing in a motion graphic a viewer takes away as fact, and hand-typed bar
 * heights look exactly as convincing as derived ones — so a chart declares its figures and points at
 * the shapes that encode them, and this compares the two.
 *
 * It asks the author for both halves on purpose. An earlier version declared the figures and then
 * *worked out* which shapes encoded them, from depth, sibling order, alignment and tag names. Across
 * seven real builds that inference was wrong in a new way each time — the wrappers around bars, the
 * `<defs>` beside them, a card sized by its type, a bar for a zero, a ring of tick marks, the
 * composition root, two dials whose figures live in their arcs — and each wrong reading blocked a
 * write on a graphic that was drawn correctly. There is no reading of a rendered box that says
 * "this length is that number"; only the author knows. So the author says.
 */
import { describeElement } from './measure';
import type { ElementMeasurement, Rect } from './measure';
import type { RuntimeLintContext, RuntimeLintRule } from './runtimeRules';

const OPACITY_PRESENT_THRESHOLD = 0.05;
/** How far a drawn shape may sit from what it claims to encode, as a fraction of the largest. */
const ENCODING_TOLERANCE = 0.05;
/** At this share of the canvas an element is the frame the graphic sits in, not a chart in it. */
const FULL_CANVAS_FRACTION = 0.9;
/** How far a line may wander and still read as flat, for a series that does not move. */
const FLAT_LINE_TOLERANCE_PX = 1;

const VALUES_ATTR = 'data-l5-values';
const BAR_ATTR = 'data-l5-bar';
const SERIES_ATTR = 'data-l5-series';
const NOT_DATA_ATTR = '[data-l5-not-data]';

/**
 * How a declaration is written, and what to say when it is written some other way. A list of numbers
 * in JSON — `data-l5-values="[8000, 19000]"` — because a comma cannot then do two jobs at once: as a
 * bare string, `8,500` is one figure to a person and two to a parser.
 */
export function declarationProblem(raw: string): string | null {
    let parsed: unknown;
    try {
        parsed = JSON.parse(raw);
    } catch {
        const bare = raw.trim();
        return (
            `its ${VALUES_ATTR} is \`${bare}\`, which is not a JSON list. Write the figures as one ` +
            `— ${VALUES_ATTR}="[${bare.replace(/[^0-9.\-,\s]/g, '')}]" — so a comma between values ` +
            `is never mistaken for a comma inside one.`
        );
    }
    if (!Array.isArray(parsed) || parsed.length === 0) {
        return `its ${VALUES_ATTR} is not a list of figures. Write it as ${VALUES_ATTR}="[8000, 19000]".`;
    }
    const quoted = parsed.find((value) => typeof value === 'string');
    if (quoted !== undefined) {
        return (
            `its ${VALUES_ATTR} holds ${JSON.stringify(quoted)} as text. A figure is a number ` +
            `here: write ${String(quoted).replace(/[^0-9.-]/g, '')} without the quotes or the ` +
            `separator, so the shape can be measured against it.`
        );
    }
    if (!parsed.every((value) => typeof value === 'number' && Number.isFinite(value))) {
        return `its ${VALUES_ATTR} holds something that is not a figure. Write it as ${VALUES_ATTR}="[8000, 19000]".`;
    }
    return null;
}

/** The declared series on an element, or null when it declares none — or declares it unreadably. */
export function declaredValues(element: Element): number[] | null {
    const raw = element.getAttribute(VALUES_ATTR);
    if (raw === null || declarationProblem(raw)) return null;
    return JSON.parse(raw) as number[];
}

/** A shape the author marked as drawn from the figures: where it sits is the data's business. */
export function isPlacedByData(element: Element): boolean {
    return element.hasAttribute(BAR_ATTR) || element.hasAttribute(SERIES_ATTR);
}

/** An element the author has marked as carrying no data — a staircase motif, equaliser bars. */
export function isMarkedDecorative(element: Element): boolean {
    return Boolean(element.closest(NOT_DATA_ATTR));
}

/**
 * Whether this element is the frame the graphic sits in rather than something drawn in it. Every
 * composition has a full-canvas wrapper, and a rule that reports the root on every graphic is a rule
 * an author learns to skip.
 */
export function isCompositionFrame(
    rect: Rect,
    canvasSize?: { width: number; height: number },
): boolean {
    if (!canvasSize) return false;
    return (
        rect.width >= canvasSize.width * FULL_CANVAS_FRACTION &&
        rect.height >= canvasSize.height * FULL_CANVAS_FRACTION
    );
}

/**
 * Whether the author has said this element plots a series: two or more figures declared on it. One
 * figure is a counter, not a chart. Marking the shapes is what lets the *lengths* be checked; this
 * says only that the thing is a chart, which is all a rule about where charts go needs to know.
 */
export function declaresSeries(element: Element): boolean {
    if (isMarkedDecorative(element)) return false;
    const values = declaredValues(element);
    return values !== null && values.length >= 2;
}

/**
 * Every frame that shows all of the element's marked shapes at once, oldest first.
 *
 * All of them, because a chart is only claiming a proportion at the moments it is fully drawn: bars
 * grow on a stagger and leave on one, so a probe during either measures lengths that are a fact
 * about that instant. One frame reading correctly is the chart encoding its figures; the rule needs
 * a reason to look at the others only when none does.
 */
function markedReadings(
    element: Element,
    context: RuntimeLintContext,
): { shapes: ElementMeasurement[]; progress: number }[] {
    const marked = Array.from(element.querySelectorAll(`[${BAR_ATTR}]`));
    if (marked.length === 0) return [];
    const readings = [];
    for (const frame of context.frames ?? []) {
        const byElement = new Map(frame.elements.map((m) => [m.element, m]));
        const shapes = marked
            .map((bar) => byElement.get(bar))
            .filter(
                (m): m is ElementMeasurement =>
                    m !== undefined && m.opacity >= OPACITY_PRESENT_THRESHOLD,
            );
        if (shapes.length === marked.length) {
            readings.push({ shapes, progress: frame.progress });
        }
    }
    return readings;
}

/** Whether this reading of the shapes is the declared series drawn faithfully. */
function encodes(shapes: ElementMeasurement[], values: number[]): boolean {
    return drawsSignsApart(shapes, values) && encodesProportionally(lengthsOf(shapes), values);
}

/** The axis that carries the quantity: the one whose lengths actually differ. */
function lengthsOf(shapes: ElementMeasurement[]): number[] {
    const spread = (values: number[]): number => Math.max(...values) - Math.min(...values);
    const widths = shapes.map((m) => m.rect.width);
    const heights = shapes.map((m) => m.rect.height);
    return spread(widths) > spread(heights) ? widths : heights;
}

/**
 * Whether the bars encode the values: each length in the same ratio to the longest as its figure is
 * to the largest. Magnitudes, because a length is never negative — a chart of losses draws how big
 * each loss is and says which way it goes by where it sits.
 */
function encodesProportionally(lengths: number[], values: number[]): boolean {
    const maxLength = Math.max(...lengths);
    const magnitudes = values.map(Math.abs);
    const maxValue = Math.max(...magnitudes);
    if (maxLength <= 0 || maxValue <= 0) return false;
    return lengths.every(
        (length, i) =>
            Math.abs(length / maxLength - magnitudes[i] / maxValue) <= ENCODING_TOLERANCE,
    );
}

/**
 * Whether a series that crosses zero is drawn on both sides of one line. Lengths are magnitudes, so
 * direction is all that is left to say which way a figure went, and a loss grown upward from the
 * same anchor as a gain reads as a gain. Only for a mixed series: an all-negative one labelled
 * "losses" is legitimately drawn as lengths.
 */
function drawsSignsApart(shapes: ElementMeasurement[], values: number[]): boolean {
    const negatives = shapes.filter((_, i) => values[i] < 0);
    const positives = shapes.filter((_, i) => values[i] > 0);
    if (negatives.length === 0 || positives.length === 0) return true;
    const horizontal =
        Math.max(...shapes.map((m) => m.rect.width)) -
            Math.min(...shapes.map((m) => m.rect.width)) >
        Math.max(...shapes.map((m) => m.rect.height)) -
            Math.min(...shapes.map((m) => m.rect.height));
    const edges = horizontal
        ? [...positives.map((m) => m.rect.x), ...negatives.map((m) => m.rect.x + m.rect.width)]
        : [...positives.map((m) => m.rect.y + m.rect.height), ...negatives.map((m) => m.rect.y)];
    return Math.max(...edges) - Math.min(...edges) <= 4;
}

function ratios(series: number[]): string {
    const max = Math.max(...series.map(Math.abs));
    return series.map((value) => (max > 0 ? (Math.abs(value) / max).toFixed(2) : '0')).join(', ');
}

export const chartValuesMismatch: RuntimeLintRule = {
    id: 'chart-values-mismatch',
    severity: 'error',
    guidance:
        'Declare a chart\'s figures on its container as `data-l5-values="[v1, v2, …]"`, mark each ' +
        'shape you draw from them with `data-l5-bar`, and compute the lengths from the array — ' +
        'never type one. The two together are what let the drawing be held to the data; a shape ' +
        'that does not encode the figure it claims is a fabricated chart, and a chart is the one ' +
        'thing on screen a viewer takes as fact. A shape whose figure is not its length — a dial ' +
        'sweeping an arc, a counter writing a number out — is not a bar and is not marked.',
    check(context: RuntimeLintContext): string[] {
        const seen = new Set<Element>();
        const messages: string[] = [];

        for (const frame of context.frames ?? []) {
            for (const { element } of frame.elements) {
                if (seen.has(element) || isMarkedDecorative(element)) continue;
                const values = declaredValues(element);
                if (!values || values.length < 2) continue;
                seen.add(element);

                const markCount = element.querySelectorAll(`[${BAR_ATTR}]`).length;
                if (markCount === 0) continue;
                if (markCount !== values.length) {
                    messages.push(
                        `${describeElement(element)} declares ${values.length} figures ` +
                            `(${values.join(', ')}) but marks ${markCount} shape(s) with ` +
                            `${BAR_ATTR}. Mark one shape per figure, in the order they are ` +
                            `declared, so each can be measured against the figure it draws.`,
                    );
                    continue;
                }

                const readings = markedReadings(element, context);
                if (readings.length === 0) continue;
                if (readings.some(({ shapes }) => encodes(shapes, values))) continue;

                const { shapes, progress } = readings[readings.length - 1];
                const lengths = lengthsOf(shapes);
                if (!drawsSignsApart(shapes, values)) {
                    messages.push(
                        `${describeElement(element)} declares ${values.join(', ')}, which crosses ` +
                            `zero, but every shape is drawn the same way from one edge — so a fall ` +
                            `reads as a rise. Anchor the series to a zero line and let the ` +
                            `negative figures hang below it.`,
                    );
                    continue;
                }
                messages.push(
                    `${describeElement(element)} declares ${values.join(', ')} but its marked ` +
                        `shapes are drawn at ${ratios(lengths)} of the longest at progress ` +
                        `${progress}, where the figures are ${ratios(values)}. Compute each ` +
                        `length from the declared array (\`const values = ` +
                        `JSON.parse(el.dataset.l5Values)\`) instead of typing it.`,
                );
            }
        }
        return messages;
    },
};

/**
 * A declaration written in a way nothing can read. Without this the attribute simply does not
 * count: the shapes go unchecked, and so does the rule that holds a graphic to the figures its plan
 * approved — a graphic could plot anything by misspelling how it says what it plots. No `guidance`,
 * because the format is already stated where a chart is authored; this is here to make getting it
 * wrong loud rather than silent.
 */
export const chartValuesUnreadable: RuntimeLintRule = {
    id: 'chart-values-unreadable',
    severity: 'error',
    check(context: RuntimeLintContext): string[] {
        const seen = new Set<Element>();
        const messages: string[] = [];
        for (const frame of context.frames ?? []) {
            for (const { element } of frame.elements) {
                if (seen.has(element) || isMarkedDecorative(element)) continue;
                const raw = element.getAttribute(VALUES_ATTR);
                if (raw === null) continue;
                seen.add(element);
                const problem = declarationProblem(raw);
                if (problem)
                    messages.push(`${describeElement(element)} declares figures, but ${problem}`);
            }
        }
        return messages;
    },
};

/**
 * The points of a path whose `d` is made only of straight moves, or null for anything else.
 *
 * Nothing here is inferred. A curve's data points are not in its `d` — the control points bend the
 * line between them — so a smoothed series is unreadable and says so by returning null, and the rule
 * stays quiet rather than reporting a count nobody can act on. Only the moves a generator emits for
 * a series read: `M` and `L`, in either case. A shorthand like `H` or `V` splits one data point
 * across two commands, so the points stop lining up with the figures anyway.
 */
function straightPathPoints(d: string): { x: number; y: number }[] | null {
    const tokens = d.match(/[a-zA-Z]|-?\d*\.?\d+(?:e[-+]?\d+)?/gi);
    if (!tokens) return null;
    const points: { x: number; y: number }[] = [];
    let command = '';
    let x = 0;
    let y = 0;
    for (let i = 0; i < tokens.length;) {
        const token = tokens[i];
        if (/[a-zA-Z]/.test(token)) {
            command = token;
            i++;
            continue;
        }
        const cursor = i;
        const relative = command === command.toLowerCase();
        const take = (): number | null => {
            const value = Number(tokens[i++]);
            return Number.isFinite(value) ? value : null;
        };
        switch (command.toUpperCase()) {
            case 'M':
            case 'L': {
                const dx = take();
                const dy = take();
                if (dx === null || dy === null) return null;
                x = relative ? x + dx : dx;
                y = relative ? y + dy : dy;
                break;
            }
            default:
                return null;
        }
        // Every command above consumes its coordinates, so this cannot fire today. It is here
        // because the failure it prevents is a spin on one command letter, and a synchronous
        // infinite loop cannot be caught by a test — it hangs the runner rather than failing it.
        if (i === cursor) return null;
        points.push({ x, y });
    }
    return points;
}

/**
 * Whether one run of coordinates tracks the figures, judged on the *steps* between points rather
 * than their distance from an axis: a trend line may legitimately start its scale above zero, so
 * what has to hold is one px-per-unit across every step.
 *
 * Sign-agnostic, which costs a real check and buys a bigger one. A chart drawn upside down — a fall
 * plotted as a rise — passes, because nothing in the geometry says which way the axis runs and only
 * convention says up means more. Holding it to that convention would report a rank or a golf score
 * plotted so that better is higher, which is a correct chart; a blocking error on a correct chart is
 * the more expensive of the two mistakes.
 */
function stepsTrack(coordinates: number[], values: number[]): boolean {
    const steps: number[] = [];
    const deltas: number[] = [];
    for (let i = 1; i < coordinates.length; i++) {
        steps.push(coordinates[i] - coordinates[i - 1]);
        deltas.push(values[i] - values[i - 1]);
    }
    const maxDelta = Math.max(...deltas.map(Math.abs));
    const maxStep = Math.max(...steps.map(Math.abs));
    // A series that does not move is drawn correctly by a line that does not move.
    if (maxDelta === 0) return maxStep <= FLAT_LINE_TOLERANCE_PX;
    if (maxStep <= 0) return false;
    const matches = (sign: number): boolean =>
        steps.every(
            (step, i) =>
                Math.abs(step / maxStep - (sign * deltas[i]) / maxDelta) <= ENCODING_TOLERANCE,
        );
    return matches(1) || matches(-1);
}

/** The marked series paths in this document, each with the figures it is drawn from. */
function markedSeries(document: Document): { path: Element; values: number[] }[] {
    const declared = new Map<Element, { path: Element; values: number[] } | null>();
    for (const path of Array.from(document.querySelectorAll(`[${SERIES_ATTR}]`))) {
        if (isMarkedDecorative(path)) continue;
        const source = path.closest(`[${VALUES_ATTR}]`);
        if (!source) continue;
        const values = declaredValues(source);
        if (!values) continue;
        // Two paths drawn from one declaration: which series carries which figures is not something
        // to guess at. Declare the figures on each path instead.
        declared.set(source, declared.has(source) ? null : { path, values });
    }
    return Array.from(declared.values()).filter(
        (entry): entry is { path: Element; values: number[] } => entry !== null,
    );
}

export const trendShapeMismatch: RuntimeLintRule = {
    id: 'trend-shape-mismatch',
    severity: 'error',
    guidance:
        'A trend line drawn from declared figures carries `data-l5-series`, and every vertex of ' +
        'its `d` is computed from the array — a typed `d` is a drawing of a trend rather than a ' +
        'chart of one. Two series in one chart declare their figures on each path.',
    check(context: RuntimeLintContext): string[] {
        const messages: string[] = [];
        for (const { path, values } of markedSeries(context.document)) {
            const points = straightPathPoints(path.getAttribute('d') ?? '');
            if (!points || points.length !== values.length) continue;
            if (
                stepsTrack(
                    points.map((point) => point.y),
                    values,
                ) ||
                stepsTrack(
                    points.map((point) => point.x),
                    values,
                )
            ) {
                continue;
            }
            messages.push(
                `${describeElement(path)} is drawn from ${values.join(', ')}, but its vertices do ` +
                    `not step the way those figures do — the line's rises and falls are not in ` +
                    `proportion to the changes it plots. Compute every vertex from the declared ` +
                    `array (\`const values = JSON.parse(chart.dataset.l5Values)\`) rather than ` +
                    `typing the \`d\`.`,
            );
        }
        return messages;
    },
};

export const CORE_DATA_LINT_RULES: RuntimeLintRule[] = [
    chartValuesUnreadable,
    chartValuesMismatch,
    trendShapeMismatch,
];
